import { validateStunServers } from "./collaboration-protocol";
import { scriptRepository, settingsRepository } from "./db";
import type { AppSettings, ScriptDocument } from "./types";

export const defaultAppSettings: AppSettings = {
  theme: "system",
  defaultLanguage: navigator.language || "en-GB",
  defaultVoiceId: undefined,
  defaultPlaybackSpeed: 1,
  recognitionThreshold: 0.4,
  ignoredWords: ["um", "uh", "er"],
  largerText: false,
  openDyslexic: false,
  highContrast: false,
  reduceMotion: false,
  betaSmartMemorisation: false,
  scriptFolders: [],
  stunServers: ["stun:stun.cloudflare.com:3478"],
  collaborationName: "",
  enableCollaborativeRehearsal: false,
  characterVoiceDefaults: {},
};

interface AppState { scripts: ScriptDocument[]; settings: AppSettings; ready: boolean }
type Listener = (state: Readonly<AppState>) => void;

interface ScriptStorePort {
  list(): Promise<ScriptDocument[]>;
  save(script: ScriptDocument): Promise<IDBValidKey>;
  delete(id: string): Promise<undefined>;
}

interface SettingsStorePort {
  get(): Promise<AppSettings | undefined>;
  save(settings: AppSettings): Promise<IDBValidKey>;
}

export class AppStore extends EventTarget {
  constructor(
    private readonly scripts: ScriptStorePort = scriptRepository,
    private readonly settings: SettingsStorePort = settingsRepository,
  ) {
    super();
  }
  state: AppState = { scripts: [], settings: { ...defaultAppSettings }, ready: false };
  private loadPromise?: Promise<void>;

  load(): Promise<void> {
    this.loadPromise ??= this.loadData();
    return this.loadPromise;
  }

  private async loadData(): Promise<void> {
    const [scripts, savedSettings] = await Promise.all([this.scripts.list(), this.settings.get()]);
    this.state = {
      scripts: scripts.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
      settings: { ...defaultAppSettings, ...savedSettings, scriptFolders: savedSettings?.scriptFolders ?? [], stunServers: normalizedStunServers(savedSettings?.stunServers), characterVoiceDefaults: savedSettings?.characterVoiceDefaults ?? {} },
      ready: true,
    };
    this.emit();
  }

  async save(script: ScriptDocument): Promise<void> {
    script.updatedAt = new Date().toISOString();
    await this.scripts.save(script);
    const scripts = this.state.scripts.filter((item) => item.id !== script.id);
    this.state = { ...this.state, scripts: [script, ...scripts] };
    this.emit();
  }

  async saveSettings(settings: AppSettings): Promise<void> {
    this.state = { ...this.state, settings };
    this.emit();
    await this.settings.save(settings);
  }

  async remove(id: string): Promise<void> {
    await this.scripts.delete(id);
    this.state = { ...this.state, scripts: this.state.scripts.filter((item) => item.id !== id) };
    this.emit();
  }

  subscribe(listener: Listener): () => void {
    const handler = () => listener(this.state);
    this.addEventListener("change", handler);
    listener(this.state);
    return () => this.removeEventListener("change", handler);
  }

  private emit(): void { this.dispatchEvent(new Event("change")); }
}

function normalizedStunServers(values: string[] | undefined): string[] {
  try { return validateStunServers(values ?? defaultAppSettings.stunServers); }
  catch { return [...defaultAppSettings.stunServers]; }
}

export const store = new AppStore();
